import { isApiError } from '@saay/api-client';
import {
  formatCount,
  formatLocalDate,
  formatToman,
  type LocalDate,
  normalizeDigits,
  toPersianDigits,
} from '@saay/formatting';
import {
  Banner,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  LoadMore,
  Ltr,
  PageHeader,
  Section,
  Skeleton,
  TextArea,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { z } from 'zod';

import type { WriteOffCandidate, WriteOffRequestCreate, WriteOffsPosted } from '../api.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { FilterPillBar, TextFilterPill } from '../components/filter-pills.js';
import { CommandDialog } from '../components/command-dialog.js';
import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { ListFooter } from '../components/list-footer.js';
import { FormReasonChips } from '../components/reason-field.js';
import { approvalSubmitLabel, canUse } from '../permissions.js';
import {
  listStatus,
  setupKeys,
  useAdminSelf,
  useWriteOffCandidates,
  type WriteOffCandidateFilters,
} from '../queries.js';

const REASON_MAX = 500;
const MAX_CASES = 100;

function localDate(value: string): LocalDate {
  return value as LocalDate;
}

function integerDays(raw: string): number | undefined {
  const digits = normalizeDigits(raw).trim();
  if (!/^\d+$/.test(digits)) return undefined;
  return Number(digits);
}

const writeOffSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'دلیل را بنویسید.')
    .max(REASON_MAX, `حداکثر ${toPersianDigits(REASON_MAX)} کاراکتر.`),
});

type WriteOffValues = z.input<typeof writeOffSchema>;

function writeOffGuidance(error: unknown): ReactNode {
  if (!isApiError(error)) return undefined;
  const details = error.details ?? {};
  switch (error.code) {
    case 'WRITE_OFF_NOT_ELIGIBLE':
      return 'بعضی از هفته‌های انتخاب‌شده دیگر نامزد سوخت نیستند. فهرست را تازه کنید.';
    case 'APPROVAL_REQUEST_PENDING':
      return typeof details.approvalRequestId === 'string' ? (
        <>
          {'درخواست سوخت دیگری در انتظار تأیید است. '}
          <Link to={`/approvals/${details.approvalRequestId}`}>مشاهدهٔ درخواست</Link>
        </>
      ) : (
        'درخواست سوخت دیگری در انتظار تأیید است.'
      );
    default:
      return undefined;
  }
}

function WriteOffDialog({
  caseIds,
  onOpenChange,
  onPosted,
}: {
  readonly caseIds: readonly string[];
  readonly onOpenChange: (open: boolean) => void;
  readonly onPosted: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const self = useAdminSelf().data;
  return (
    <CommandDialog<WriteOffValues, WriteOffRequestCreate, WriteOffsPosted>
      defaultValues={{ reason: '' }}
      description={`${formatCount(caseIds.length)} هفته انتخاب شده است. سوخت کل هفته را می‌پوشاند؛ اقساط معوق می‌مانند و وصول ادامه دارد.`}
      fields={['reason']}
      guidance={writeOffGuidance}
      name="delinquency.write-off"
      onApprovalPending={async () => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.approvals });
        await queryClient.invalidateQueries({ queryKey: setupKeys.writeOffCandidates });
      }}
      onOpenChange={onOpenChange}
      onSuccess={async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.writeOffCandidates });
        await queryClient.invalidateQueries({ queryKey: setupKeys.delinquencyCases });
        toast.success({
          confirmedBy: result,
          description: `${formatCount(result.data.writeOffs.length)} هفته ثبت شد.`,
          title: 'سوخت مطالبات ثبت و اجرا شد',
        });
        onPosted();
        onOpenChange(false);
      }}
      schema={writeOffSchema}
      send={(client, body, intent) => client.post('/admin/write-off-requests', { body, intent })}
      submitLabel={self === undefined ? 'ثبت و اجرا' : approvalSubmitLabel(self)}
      title="درخواست سوخت مطالبات"
      toBody={(values) => ({
        delinquencyCaseIds: [...caseIds],
        reason: values.reason.trim(),
      })}
      variant="danger"
    >
      {(form) => (
        <>
          <TextArea
            error={form.formState.errors.reason?.message}
            label="دلیل"
            maxLength={REASON_MAX}
            {...form.register('reason')}
          />
          <FormReasonChips form={form} intent="delinquency.write-off" />
        </>
      )}
    </CommandDialog>
  );
}

export function WriteOffsPage() {
  usePageTitle('سوخت مطالبات');
  return (
    <RequireArea area="writeOffs">
      {(self) => <WriteOffCandidateList canRequest={canUse(self, 'requestWriteOff')} />}
    </RequireArea>
  );
}

function WriteOffCandidateList({ canRequest }: { readonly canRequest: boolean }) {
  const [params, setParams] = useSearchParams();
  const daysFromRaw = params.get('daysDelinquentFrom') ?? '';
  const daysFrom = integerDays(daysFromRaw);
  const filters: WriteOffCandidateFilters =
    daysFrom === undefined ? {} : { daysDelinquentFrom: daysFrom };
  const list = useWriteOffCandidates(filters);
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [open, setOpen] = useState(false);
  const tooMany = selected.length > MAX_CASES;
  const toggle = (id: string, checked: boolean) => {
    setSelected((current) =>
      checked
        ? current.includes(id)
          ? current
          : [...current, id]
        : current.filter((item) => item !== id),
    );
  };
  const columns: readonly DataTableColumn<WriteOffCandidate>[] = [
    ...(canRequest
      ? [
          {
            header: 'انتخاب',
            id: 'select',
            render: (row: WriteOffCandidate) => (
              <Checkbox
                checked={selected.includes(row.delinquencyCaseId)}
                label={`انتخاب ${row.buyer.displayNameFa}`}
                onChange={(event) => toggle(row.delinquencyCaseId, event.target.checked)}
                onClick={(event) => event.stopPropagation()}
              />
            ),
          } satisfies DataTableColumn<WriteOffCandidate>,
        ]
      : []),
    {
      header: 'خریدار',
      id: 'buyer',
      pin: 'start',
      render: (row) => (
        <Link to={`/delinquency-cases/${row.delinquencyCaseId}`}>{row.buyer.displayNameFa}</Link>
      ),
    },
    {
      header: 'هفته',
      id: 'week',
      render: (row) => formatLocalDate(localDate(row.weekStart)),
    },
    {
      header: 'شروع دیرکرد',
      id: 'from',
      render: (row) => formatLocalDate(localDate(row.delinquentFrom)),
    },
    {
      header: 'روز معوق',
      id: 'days',
      render: (row) => `${formatCount(row.daysDelinquent)} روز`,
    },
    {
      header: 'اصل',
      id: 'principal',
      render: (row) => formatToman(row.principalIrr),
    },
    {
      header: 'وجه التزام',
      id: 'fee',
      render: (row) => formatToman(row.lateFeeIrr),
    },
    {
      header: 'خریدها',
      id: 'purchases',
      render: (row) => formatCount(row.purchaseCount),
    },
    {
      header: 'موبایل',
      id: 'mobile',
      render: (row) => <Ltr>{row.buyer.mobileMasked}</Ltr>,
    },
  ];
  return (
    <>
      <PageHeader
        actions={
          canRequest ? (
            <Button
              disabled={selected.length === 0 || tooMany}
              onClick={() => setOpen(true)}
              type="button"
              variant="danger"
            >
              درخواست سوخت
            </Button>
          ) : undefined
        }
        description="هفته‌هایی که سرور نامزد سوخت می‌داند. سوخت کل هفته را می‌پوشاند و وصول را متوقف نمی‌کند."
        eyebrow="تعهد مستقل از وصول خریدار"
        title="سوخت مطالبات"
      />
      {tooMany ? (
        <Banner tone="warning">{`حداکثر ${formatCount(MAX_CASES)} هفته را با هم می‌توان ثبت کرد.`}</Banner>
      ) : null}
      <Section id="write-off-candidates" title="نامزدهای سوخت">
        <FilterPillBar
          activeFilterCount={daysFromRaw === '' ? 0 : 1}
          label="فیلتر نامزدهای سوخت"
          onClearAll={() => {
            const next = new URLSearchParams(params);
            next.delete('daysDelinquentFrom');
            setParams(next);
          }}
        >
          <TextFilterPill
            inputMode="numeric"
            label="حداقل روز معوق"
            onApply={(value) => {
              const next = new URLSearchParams(params);
              if (value === '') next.delete('daysDelinquentFrom');
              else next.set('daysDelinquentFrom', value);
              setParams(next);
            }}
            value={daysFromRaw}
          />
        </FilterPillBar>
        <QueryState
          empty={
            <EmptyState
              action={
                daysFromRaw === '' ? undefined : (
                  <Button onClick={() => setParams({})} variant="secondary">
                    پاک‌کردن فیلتر
                  </Button>
                )
              }
              title={
                daysFromRaw === '' ? 'نامزد سوختی نیست' : 'نامزدی با این حداقل روز معوق پیدا نشد'
              }
            />
          }
          error={list.error}
          isEmpty={rows.length === 0}
          onRetry={() => void list.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری نامزدها…" lines={4} />}
          status={listStatus(list)}
        >
          <AdminListTable
            caption="فهرست نامزدهای سوخت"
            columns={columns}
            mobileSummary={(row) => ({
              primary: row.buyer.displayNameFa,
              secondary: `${formatCount(row.daysDelinquent)} روز · ${formatToman(row.principalIrr)}`,
            })}
            regionLabel="جدول نامزدهای سوخت"
            rowKey={(row) => row.delinquencyCaseId}
            rows={rows}
          />
          {list.isFetchNextPageError ? (
            <ErrorState error={list.error} onRetry={() => void list.fetchNextPage()} />
          ) : null}
          <ListFooter
            pager={
              <LoadMore
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                onLoadMore={() => void list.fetchNextPage()}
              />
            }
            shown={rows.length}
            total={undefined}
          />
        </QueryState>
      </Section>
      {open ? (
        <WriteOffDialog
          caseIds={selected}
          onOpenChange={setOpen}
          onPosted={() => setSelected([])}
        />
      ) : null}
    </>
  );
}
